import _typeof from '@babel/runtime/helpers/typeof';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import _objectSpread from '@babel/runtime/helpers/objectSpread2';
import _PropTypes from 'prop-types';
import * as React from 'react';
import styled from 'styled-components';
import { spacing2x, spacing1x } from '../../styles/spacing.js';
import { getAdornment } from '../../utils/adornment.js';
import { mergeProps } from '../../utils/react.js';
import { jsxs, jsx } from 'react/jsx-runtime';

var _excluded = ["children", "endAdornment", "focusRef", "startAdornment"];
var StyledAdornment = styled.span.withConfig({
  displayName: "InputContainer__StyledAdornment",
  componentId: "z8h8so-0"
})(["&&{align-items:center;display:flex;", ";}"], function (props) {
  return "".concat(props.endAdornment ? "padding-left" : "padding-right", ": ").concat(spacing2x, ";");
});
var getContainerStyles = function getContainerStyles(props) {
  return _objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread({
    background: "none",
    borderColor: props.theme.neutralColors.silver,
    borderRadius: spacing1x,
    borderStyle: "solid",
    borderWidth: 1,
    color: props.theme.neutralColors.charcoal,
    fontSize: 14,
    padding: "5px ".concat(spacing2x),
    transition: "border-color 0.15s ease-in-out, background 0.15s ease-in-out"
  }, props.disabled ? {
    opacity: 0.5,
    cursor: "not-allowed",
    backgroundColor: props.theme.neutralColors.pearl
  } : {}), props.error ? {
    borderColor: props.theme.alertColors.error,
    color: props.theme.alertColors.error
  } : {}), props.success ? {
    borderColor: props.theme.alertColors.success,
    color: props.theme.alertColors.success
  } : {}), props.focused && (!props.error || !props.success) ? {
    outline: 0,
    background: props.theme.neutralColors.white,
    borderColor: props.theme.CTAColors.primary
  } : {}), !props.disabled && !props.error && !props.success && !props.focused && !props.active ? {
    ":hover:not(:focus)": {
      borderColor: props.theme.neutralColors.smoke
    }
  } : {});
};
var StyledInputContainer = styled.div.withConfig({
  displayName: "InputContainer__StyledInputContainer",
  componentId: "z8h8so-1"
})(function (props) {
  return _objectSpread(_objectSpread({}, getContainerStyles(props)), {}, {
    cursor: "text",
    display: "inline-flex",
    height: 32
  });
});
var InputContainer = /*#__PURE__*/React.forwardRef(function Input(props, ref) {
  var children = props.children,
      endAdornment = props.endAdornment,
      focusRef = props.focusRef,
      startAdornment = props.startAdornment,
      rest = _objectWithoutProperties(props, _excluded);

  var defaultFocusRef = React.useRef(null);
  var startRef = React.useRef(null);
  var endRef = React.useRef(null);

  var _React$useState = React.useState(false),
      _React$useState2 = _slicedToArray(_React$useState, 2),
      focused = _React$useState2[0],
      setFocused = _React$useState2[1];

  var handleClick = function handleClick(e) {
    if (defaultFocusRef !== null && defaultFocusRef !== void 0 && defaultFocusRef.current && (e.currentTarget === e.target || e.currentTarget === startRef.current || e.currentTarget === endRef.current)) {
      var _focusable$;

      var focusable = defaultFocusRef === null || defaultFocusRef === void 0 ? void 0 : defaultFocusRef.current.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
      if (focusRef !== null && focusRef !== void 0 && focusRef.current) focusRef.current.focus();else (_focusable$ = focusable[0]) === null || _focusable$ === void 0 ? void 0 : _focusable$.focus();
    }
  };

  var ICON_SIZE = 20;
  var startAddon = getAdornment(startAdornment, {
    size: ICON_SIZE
  });
  var endAddon = getAdornment(endAdornment, {
    size: ICON_SIZE
  });
  return /*#__PURE__*/jsxs(StyledInputContainer, _objectSpread(_objectSpread({}, mergeProps({
    onBlur: function onBlur() {
      return setFocused(false);
    },
    onFocus: function onFocus() {
      return setFocused(true);
    },
    onClick: handleClick,
    ref: defaultFocusRef
  }, _objectSpread(_objectSpread({}, rest), {}, {
    ref: ref
  }))), {}, {
    focused: focused,
    hasEndAdornment: !!props.endAdornment,
    children: [startAddon && /*#__PURE__*/jsx(StyledAdornment, {
      startAdornment: true,
      ref: startRef,
      children: startAddon
    }), children, endAddon && /*#__PURE__*/jsx(StyledAdornment, {
      endAdornment: true,
      ref: endRef,
      children: endAddon
    })]
  }));
});
InputContainer.propTypes = {
  "children": _PropTypes.node,
  "endAdornment": function checkAdornment(props, propName, componentName) {
    var isReactNodeType = function isReactNodeType(el) {
      var REACT_ELEMENT_TYPE = typeof Symbol === 'function' && Symbol.for ? Symbol.for('react.element') : 0xeac7;
      var isValidElement = _typeof(el) === 'object' && el !== null && el['\$\$typeof'] === REACT_ELEMENT_TYPE;
      return el === undefined || el === null || typeof el === 'boolean' || typeof el === 'string' || isValidElement;
    };

    var adornment = (propName === 'startAdornment' || propName === 'endAdornment') && props[propName];

    if (!isReactNodeType(adornment)) {
      var name = adornment.displayName || adornment.name || 'Component';

      if (!(name.indexOf('Icon') === 0)) {
        return new Error("Invalid prop \"" + propName + "\". Invalid React component with name \"" + name + "\" was supplied to \"" + componentName + "\". Only Icon components are allowed.\n");
      }
    }
  },
  "focusRef": _PropTypes.shape({
    current: _PropTypes.object
  }),
  "startAdornment": function checkAdornment(props, propName, componentName) {
    var isReactNodeType = function isReactNodeType(el) {
      var REACT_ELEMENT_TYPE = typeof Symbol === 'function' && Symbol.for ? Symbol.for('react.element') : 0xeac7;
      var isValidElement = _typeof(el) === 'object' && el !== null && el['\$\$typeof'] === REACT_ELEMENT_TYPE;
      return el === undefined || el === null || typeof el === 'boolean' || typeof el === 'string' || isValidElement;
    };

    var adornment = (propName === 'startAdornment' || propName === 'endAdornment') && props[propName];

    if (!isReactNodeType(adornment)) {
      var name = adornment.displayName || adornment.name || 'Component';

      if (!(name.indexOf('Icon') === 0)) {
        return new Error("Invalid prop \"" + propName + "\". Invalid React component with name \"" + name + "\" was supplied to \"" + componentName + "\". Only Icon components are allowed.\n");
      }
    }
  }
};
InputContainer.displayName = "InputContainer";

export { InputContainer, getContainerStyles };
